/**
 * 확인 다이얼로그 컴포넌트
 * - window.confirm() 대체용 스타일된 모달
 * - Esc 키 / 오버레이 클릭으로 닫기
 * - variant='destructive' 시 확인 버튼 빨간색
 */
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { dialogVariants, useSnapVariants } from '../../lib/motion';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  /** Optional rich content rendered below description (e.g. collection impact list). */
  body?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  /** Optional 보조 액션 — 표시 시 cancel과 confirm 사이에 outline 스타일로 배치 */
  secondaryText?: string;
  onSecondary?: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  body,
  confirmText = '확인',
  cancelText = '취소',
  variant = 'default',
  secondaryText,
  onSecondary,
}) => {
  const confirmRef = React.useRef<HTMLButtonElement>(null);
  const snapDialog = useSnapVariants(dialogVariants);

  // Esc 키로 닫기
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // 열릴 때 확인 버튼에 포커스
  React.useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => confirmRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 dark:bg-black/70"
          onClick={onClose}
        >
          <motion.div
            className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-[calc(100vw-16px)] xs:max-w-sm mx-4 max-h-[85vh] flex flex-col overflow-hidden"
            variants={snapDialog}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 스크롤 영역 */}
            <div className="overflow-y-auto p-6 flex-1">
              {/* 제목 */}
              <h2
                id="confirm-dialog-title"
                className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-2"
              >
                {title}
              </h2>

              {/* 설명 */}
              <p
                id="confirm-dialog-description"
                className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed"
              >
                {description}
              </p>

              {/* 선택적 추가 콘텐츠 (e.g. 컬렉션 영향 목록) */}
              {body && (
                <div className="mt-3">
                  {body}
                </div>
              )}
            </div>

            {/* 버튼 — sticky footer */}
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 flex-wrap">
              <Button variant="outline" size="sm" onClick={onClose} className="min-h-[44px]">
                {cancelText}
              </Button>
              {secondaryText && onSecondary && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onSecondary();
                    onClose();
                  }}
                  className="min-h-[44px]"
                >
                  {secondaryText}
                </Button>
              )}
              <Button
                ref={confirmRef}
                variant={variant === 'destructive' ? 'destructive' : 'default'}
                size="sm"
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={cn(
                  'min-h-[44px]',
                  variant === 'destructive' && 'bg-red-500 hover:bg-red-600 text-white'
                )}
              >
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
